/**
 * Hand-built SVG charts for the console (no charting library, bundled by esbuild). Every mark
 * is keyboard-focusable and carries its readout in data attributes; one tooltip, filled with
 * textContent (never innerHTML), shows it on hover and on focus. Colors are CSS tokens only.
 */

const escAttr = (s: unknown) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** One readout row of a tooltip: the value leads, the label follows, a line key in `color`. */
export interface TipRow {
  value: string;
  label: string;
  /** A CSS custom property name, e.g. "--viz-1". */
  color: string;
}

/** The readout of a mark, as a data attribute (read back by the tooltip). */
const tipAttr = (title: string, rows: TipRow[]) =>
  `data-tip="${escAttr(JSON.stringify({ title, rows }))}"`;

/** Width to draw at: the page column minus the card's padding (text stays at 1:1 scale). */
export function vizWidth(): number {
  const app = document.querySelector('#app') as HTMLElement | null;
  const w = app?.clientWidth ?? 800;
  return Math.max(300, Math.min(1160, w - 42));
}

/** A clean axis maximum and step (1, 2 or 5 × 10ⁿ) with about four ticks. */
function niceScale(max: number): { top: number; step: number } {
  if (max <= 0) return { top: 1, step: 1 };
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 5, 10].find((m) => m * pow >= raw) ?? 10) * pow;
  return { top: Math.ceil(max / step) * step, step };
}

/** A column that grows from the baseline: square at the base, 4px rounded data-end. */
function column(x: number, y: number, w: number, h: number) {
  if (h <= 0) return '';
  const r = Math.min(4, w / 2, h);
  return `M${x} ${y + h}V${y + r}Q${x} ${y} ${x + r} ${y}H${x + w - r}Q${x + w} ${y} ${x + w} ${y + r}V${y + h}Z`;
}

export interface Series {
  name: string;
  /** CSS custom property with the series color. */
  color: string;
  values: number[];
}

/**
 * Columns per month, one or two series side by side (grouped, never stacked: each value
 * reads from the baseline). One axis; ticks in `axis` format; readouts in `value` format.
 */
export function columnChart(o: {
  label: string;
  categories: { key: string; short: string; long: string }[];
  series: Series[];
  value: (minor: number) => string;
  axis: (minor: number) => string;
}): string {
  const W = vizWidth();
  const H = 230;
  const m = { top: 14, right: 8, bottom: 28, left: 64 };
  const plotW = W - m.left - m.right;
  const plotH = H - m.top - m.bottom;
  const n = o.categories.length || 1;
  const band = plotW / n;
  const max = Math.max(0, ...o.series.flatMap((s) => s.values));
  const { top, step } = niceScale(max);
  const y = (v: number) => m.top + plotH - (v / top) * plotH;
  const k = o.series.length;
  const barW = Math.max(3, Math.min(24, (band - 8) / k - 2));
  const groupW = k * barW + (k - 1) * 2;
  let grid = '';
  for (let v = 0; v <= top + step / 2; v += step)
    grid += `<line class="viz-grid${v === 0 ? ' base' : ''}" x1="${m.left}" x2="${W - m.right}" y1="${y(v).toFixed(1)}" y2="${y(v).toFixed(1)}"/>
      <text class="viz-axis" x="${m.left - 8}" y="${(y(v) + 4).toFixed(1)}" text-anchor="end">${escAttr(o.axis(v))}</text>`;
  // Month labels at least ~44px apart.
  const every = Math.max(1, Math.ceil(44 / band));
  let marks = '';
  o.categories.forEach((c, i) => {
    const x0 = m.left + i * band;
    const gx = x0 + (band - groupW) / 2;
    const rows = o.series.map((s) => ({
      value: o.value(s.values[i] ?? 0),
      label: s.name,
      color: s.color,
    }));
    const bars = o.series
      .map((s, j) => {
        const v = s.values[i] ?? 0;
        const yy = y(v);
        return `<path class="viz-bar" style="fill:var(${s.color})" d="${column(gx + j * (barW + 2), yy, barW, m.top + plotH - yy)}"/>`;
      })
      .join('');
    const aria = `${c.long}: ${rows.map((r) => `${r.label} ${r.value}`).join(', ')}`;
    marks += `<g class="viz-mark" tabindex="0" role="img" aria-label="${escAttr(aria)}" ${tipAttr(c.long, rows)}>
      <rect class="viz-hit" x="${x0.toFixed(1)}" y="${m.top}" width="${band.toFixed(1)}" height="${plotH}"/>${bars}</g>`;
    if (i % every === 0)
      marks += `<text class="viz-axis" x="${(x0 + band / 2).toFixed(1)}" y="${H - 8}" text-anchor="middle">${escAttr(c.short)}</text>`;
  });
  const legend =
    k > 1
      ? `<ul class="viz-legend">${o.series.map((s) => `<li><i class="swatch" style="background:var(${s.color})"></i>${escAttr(s.name)}</li>`).join('')}</ul>`
      : '';
  return `<figure class="viz"><svg viewBox="0 0 ${W} ${H}" role="group" aria-label="${escAttr(o.label)}">${grid}${marks}</svg>${legend}</figure>`;
}

/**
 * Where the touch targets of a row of parts meet: `edges` are the ideal boundaries (the middle
 * of each gap between two parts), moved as little as possible (least squares) so every part's
 * target is at least `min` wide. With `u[i] = edge[i] − (i + 1) · min` the rule is "u never
 * decreases", solved by pooling adjacent violators (isotonic regression); a thin part ends up
 * with a target centered on it that takes the room from its wide neighbours.
 */
function spreadEdges(edges: number[], width: number, min: number): number[] {
  const n = edges.length + 1;
  const room = width - n * min;
  const pools: { sum: number; count: number }[] = [];
  edges.forEach((e, i) => {
    let pool = { sum: e - (i + 1) * min, count: 1 };
    for (
      let last = pools.at(-1);
      last && last.sum / last.count >= pool.sum / pool.count;
      last = pools.at(-1)
    ) {
      pools.pop();
      pool = { sum: pool.sum + last.sum, count: pool.count + last.count };
    }
    pools.push(pool);
  });
  const out: number[] = [];
  for (const p of pools) {
    const u = Math.min(room, Math.max(0, p.sum / p.count));
    for (let k = 0; k < p.count; k++) out.push(u + (out.length + 1) * min);
  }
  return out;
}

/**
 * One horizontal bar split into ordered parts (e.g. receivables by days past due), with a
 * 2px surface gap between parts and a legend that names every part with its amount.
 *
 * Each part is a target (#239, WCAG 2.5.8): a transparent `rect.viz-hit`, as tall as the bar and
 * at least `H` wide (a square, so it stays over 24 px when the chart is drawn smaller than its
 * viewBox), covers the part and splits the bar's length with its neighbours without overlapping
 * them. A part thinner than that (1–30 días can be 5 px) takes its room from the wide ones.
 */
export function stackedBar(o: {
  label: string;
  parts: { name: string; color: string; amount: number; detail: string; value: string }[];
}): string {
  const W = vizWidth();
  const H = 28;
  const total = o.parts.reduce((s, p) => s + Math.max(0, p.amount), 0);
  const visible = o.parts.filter((p) => p.amount > 0);
  const gaps = Math.max(0, visible.length - 1) * 2;
  const widths = visible.map((p) => (total ? ((W - gaps) * p.amount) / total : 0));
  // Ideal edges between targets: the middle of each 2px gap.
  const middles: number[] = [];
  let end = 0;
  for (const w of widths.slice(0, -1)) {
    end += w + 2;
    middles.push(end - 1);
  }
  const edges = [0, ...spreadEdges(middles, W, Math.min(H, W / (visible.length || 1))), W].map(
    (e) => Math.round(e * 100) / 100,
  );
  let x = 0;
  const segs = visible
    .map((p, i) => {
      const w = widths[i] ?? 0;
      const first = i === 0;
      const last = i === visible.length - 1;
      const r = Math.min(4, w / 2);
      // Rounded only at the bar's two outer ends.
      const d = `M${x + (first ? r : 0)} 0H${x + w - (last ? r : 0)}${last ? `Q${x + w} 0 ${x + w} ${r}V${H - r}Q${x + w} ${H} ${x + w - r} ${H}` : `V${H}`}H${x + (first ? r : 0)}${first ? `Q${x} ${H} ${x} ${H - r}V${r}Q${x} 0 ${x + r} 0` : 'V0'}Z`;
      const rows = [{ value: p.value, label: p.detail, color: p.color }];
      const a = edges[i] ?? 0;
      const b = edges[i + 1] ?? W;
      const seg = `<g class="viz-mark" tabindex="0" role="img" aria-label="${escAttr(`${p.name}: ${p.value}, ${p.detail}`)}" ${tipAttr(p.name, rows)}>
        <rect class="viz-hit" x="${a}" y="0" width="${(b - a).toFixed(2)}" height="${H}"/><path class="viz-bar" style="fill:var(${p.color})" d="${d}"/></g>`;
      x += w + 2;
      return seg;
    })
    .join('');
  const empty = total
    ? ''
    : `<rect class="viz-track" x="0" y="0" width="${W}" height="${H}" rx="4"/>`;
  const legend = `<ul class="viz-legend">${o.parts
    .map(
      (p) =>
        `<li><i class="swatch" style="background:var(${p.color})"></i>${escAttr(p.name)} <strong class="num">${escAttr(p.value)}</strong></li>`,
    )
    .join('')}</ul>`;
  return `<figure class="viz"><svg class="viz-stack" viewBox="0 0 ${W} ${H}" role="group" aria-label="${escAttr(o.label)}">${empty}${segs}</svg>${legend}</figure>`;
}

let tip: HTMLDivElement | null = null;

function showTip(mark: Element, at: { x: number; y: number }) {
  const raw = mark.getAttribute('data-tip');
  if (!raw) return;
  const data = JSON.parse(raw) as { title: string; rows: TipRow[] };
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'viz-tip';
    tip.setAttribute('role', 'tooltip');
    document.body.append(tip);
  }
  tip.replaceChildren();
  const title = document.createElement('div');
  title.className = 'viz-tip-title';
  title.textContent = data.title;
  tip.append(title);
  for (const r of data.rows) {
    const row = document.createElement('div');
    row.className = 'viz-tip-row';
    const key = document.createElement('i');
    key.style.background = `var(${r.color})`;
    const value = document.createElement('strong');
    value.textContent = r.value;
    const label = document.createElement('span');
    label.textContent = r.label;
    row.append(key, value, label);
    tip.append(row);
  }
  tip.hidden = false;
  const { width, height } = tip.getBoundingClientRect();
  const left = Math.min(window.innerWidth - width - 8, Math.max(8, at.x - width / 2));
  const top = at.y - height - 12 < 8 ? at.y + 16 : at.y - height - 12;
  tip.style.left = `${left}px`;
  tip.style.top = `${top}px`;
}

const hideTip = () => {
  if (tip) tip.hidden = true;
};

/** Hover and focus readouts for every chart inside `root` (bound once). */
export function bindChartTooltips(root: HTMLElement) {
  root.addEventListener('pointermove', (ev) => {
    const mark = (ev.target as Element).closest?.('.viz-mark');
    if (mark) showTip(mark, { x: ev.clientX, y: ev.clientY });
    else hideTip();
  });
  root.addEventListener('pointerleave', hideTip);
  root.addEventListener('focusin', (ev) => {
    const mark = (ev.target as Element).closest?.('.viz-mark');
    if (!mark) return hideTip();
    const b = mark.getBoundingClientRect();
    showTip(mark, { x: b.left + b.width / 2, y: b.top });
  });
  root.addEventListener('focusout', hideTip);
  window.addEventListener('scroll', hideTip, { passive: true });
}
